import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Filter, ArrowRight, Sparkles } from "lucide-react";
import api from "../lib/api";
import SafeImage from "../components/site/SafeImage";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { breadcrumb, graph } from "../lib/schema";

// Master category list used for filter chips + for admin dropdown parity.
// Organised in two intuitive groups: imaging modality first, then
// subspecialty categories that patients actually search for.
export const CASE_CATEGORIES = [
  "All",
  // Imaging modality
  "Slit Lamp / Anterior Segment",
  "Fundus Photography",
  "OCT (Retina)",
  "OCT (Anterior / Cornea)",
  "OCT / HVF (Glaucoma)",
  "Fluorescein Angiography",
  "Corneal Topography",
  "Specular Microscopy",
  "Ultrasound (B-scan)",
  // Subspecialty / clinical area
  "Glaucoma",
  "Squint / Strabismus",
  "Oculoplasty & Adnexa",
  "Neuro-Ophthalmology",
  "Pediatric Ophthalmology",
  // Context
  "Intra-operative / Surgical",
  "External / Clinical Photograph",
  "Other",
];

const DOCTOR_LABEL = {
  shantanu: "Dr. Shantanu Kumar Gupta",
  manisha: "Dr. Manisha Mishra",
  team: "Shantanu Netralaya clinical team",
};

const CaseCard = ({ c }) => (
  <Link
    to={`/eye-cases/${c.slug}`}
    className="group card-elegant overflow-hidden flex flex-col"
    data-testid={`case-card-${c.slug}`}
  >
    <div className="aspect-[4/3] bg-[hsl(var(--secondary))]/50 overflow-hidden">
      {c.image_url ? (
        <SafeImage
          src={c.image_url}
          alt={c.image_alt || c.title}
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[hsl(var(--accent))]/60">
          <Camera size={40} strokeWidth={1.25} />
        </div>
      )}
    </div>
    <div className="p-6 flex flex-col flex-1">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] mb-2 flex items-center gap-2">
        {c.featured && <Sparkles size={11} className="text-[hsl(var(--accent))]" aria-hidden="true" />}
        {c.category}
      </div>
      <h3 className="font-serif text-xl leading-snug mb-2">{c.title}</h3>
      {c.summary && (
        <p className="text-sm text-muted-foreground leading-relaxed">{c.summary}</p>
      )}
      {c.diagnosis && (
        <div className="mt-4 inline-flex items-center gap-2 self-start px-2.5 py-1 rounded-full bg-[hsl(var(--primary))]/8 text-[11px] text-[hsl(var(--primary))]">
          Dx · {c.diagnosis}
        </div>
      )}
      <div className="mt-auto pt-5 flex items-center justify-between gap-3">
        {c.submitted_by && DOCTOR_LABEL[c.submitted_by] && (
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">
            by {DOCTOR_LABEL[c.submitted_by]}
          </span>
        )}
        <span className="ml-auto text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))] inline-flex items-center">
          Open case <ArrowRight size={12} className="ml-1.5 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </div>
  </Link>
);

const EyeCases = () => {
  usePageMeta({
    title: "Eye Case Studies — Slit Lamp, Fundus, OCT | Dr. Shantanu Kumar Gupta, Varanasi",
    path: "/eye-cases",
    description:
      "Real clinical imaging cases — slit lamp, fundus photography, OCT, FFA and corneal topography — annotated by Dr. Shantanu Kumar Gupta, MBBS, MS (MAMC), DNB. Educational atlas for patients and doctors.",
  });
  usePageJsonLd(
    graph([
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Eye Case Studies", path: "/eye-cases" },
      ]),
    ])
  );

  const [cases, setCases] = useState([]);
  const [active, setActive] = useState("All");

  useEffect(() => {
    api.get("/cases").then((r) => setCases(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (active === "All") return cases;
    return cases.filter((c) => c.category === active);
  }, [cases, active]);

  const categoriesWithCounts = useMemo(() => {
    const counts = new Map();
    cases.forEach((c) => counts.set(c.category, (counts.get(c.category) || 0) + 1));
    return CASE_CATEGORIES.map((cat) => ({
      cat,
      count: cat === "All" ? cases.length : counts.get(cat) || 0,
    })).filter(({ cat, count }) => cat === "All" || count > 0);
  }, [cases]);

  return (
    <div data-testid="eye-cases-page">
      <section className="pt-20 pb-8 container-page">
        <div className="overline mb-4 inline-flex items-center gap-2">
          <Camera size={12} /> Clinical imaging atlas
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          Eye Case Studies — real clinical images, annotated
        </h1>
        <p className="mt-5 max-w-2xl prose-elegant">
          A growing library of anonymised patient cases from our clinic — slit-lamp, fundus, OCT, FFA and topography imaging with the same
          interpretation and teaching notes we share with our post-graduate students. Educational only; not a substitute for a formal consultation.
        </p>
        <div className="mt-6 inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
          <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))]" />
          {cases.length} case{cases.length === 1 ? "" : "s"} published · updated regularly
        </div>
      </section>

      {categoriesWithCounts.length > 1 && (
        <section className="pb-6 container-page">
          <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            <Filter size={11} /> Filter by imaging modality
          </div>
          <div className="flex flex-wrap gap-2" role="tablist" data-testid="case-filter-chips">
            {categoriesWithCounts.map(({ cat, count }) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                data-testid={`case-filter-${cat.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className={`text-xs px-3.5 py-1.5 rounded-full border transition-colors ${
                  active === cat
                    ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                    : "border-border text-muted-foreground hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))]/50"
                }`}
                aria-pressed={active === cat}
              >
                {cat} <span className="opacity-70 ml-1">({count})</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="pb-24 container-page">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground text-sm" data-testid="cases-empty-state">
            No cases published in this category yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((c) => (
              <CaseCard key={c.slug} c={c} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default EyeCases;
